import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Fade, Slide } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CelebrationIcon from '@mui/icons-material/Celebration';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const PLING_MESSAGES = [
  'Nytt lead i pipeline! 🎯',
  'Jobbet lönar sig! 🚀',
  'En till möjlighet! ⭐',
  'Pipeline växer! 📈',
];

const KLING_MESSAGES = [
  'GRATTIS! Vilken prestation! 🥇',
  'FANTASTISKT! Affären är vunnen! 🏆',
  'YES! Ni är bäst! 🎊',
  'IMPONERANDE! Sälj-mästare! 👑',
];

function randomFrom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Confetti particle
function Particle({ color, delay, left, size }) {
  return (
    <Box sx={{
      position: 'absolute',
      left: `${left}%`,
      top: '-10px',
      width: size,
      height: size,
      borderRadius: Math.random() > 0.5 ? '50%' : '2px',
      bgcolor: color,
      animation: `confettiFall 2.5s ease-in ${delay}s forwards`,
      '@keyframes confettiFall': {
        '0%': { transform: 'translateY(0) rotate(0deg)', opacity: 1 },
        '100%': { transform: `translateY(300px) rotate(${Math.random() * 720 - 360}deg)`, opacity: 0 },
      },
    }} />
  );
}

const CONFETTI_COLORS = ['#FF6B00', '#FFD700', '#FF6B6B', '#4CAF50', '#2196F3', '#9C27B0', '#FF8C00'];

export default function PlingCelebration({ type, leadTitle, companyName, value, onClose }) {
  const [visible, setVisible] = useState(true);
  const [message] = useState(
    type === 'kling-klang' ? randomFrom(KLING_MESSAGES) : randomFrom(PLING_MESSAGES)
  );

  const isPling = type === 'pling';
  const autoCloseMs = isPling ? 4000 : 7000;

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 500);
    }, autoCloseMs);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  const particles = isPling ? [] : Array.from({ length: 40 }, (_, i) => ({
    id: i,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay: Math.random() * 0.8,
    left: Math.random() * 100,
    size: `${6 + Math.random() * 8}px`,
  }));

  if (isPling) {
    // Subtle slide-in toast bottom right
    return (
      <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
        <Box sx={{
          position: 'fixed', bottom: 32, right: 32, zIndex: 9999,
          cursor: 'pointer',
        }} onClick={() => { setVisible(false); setTimeout(onClose, 300); }}>
          <Paper elevation={8} sx={{
            p: 2.5, borderRadius: 3, minWidth: 280, maxWidth: 360,
            borderLeft: '5px solid #FF6B00',
            bgcolor: '#1a1a1a', color: 'white',
            animation: 'ringBell 0.6s ease 0.3s',
            '@keyframes ringBell': {
              '0%':   { transform: 'rotate(0deg)' },
              '15%':  { transform: 'rotate(-12deg)' },
              '30%':  { transform: 'rotate(12deg)' },
              '45%':  { transform: 'rotate(-8deg)' },
              '60%':  { transform: 'rotate(8deg)' },
              '75%':  { transform: 'rotate(-4deg)' },
              '100%': { transform: 'rotate(0deg)' },
            },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <NotificationsActiveIcon sx={{
                fontSize: 32, color: '#FF6B00', flexShrink: 0,
                animation: 'shake 0.5s ease 0.2s',
                '@keyframes shake': {
                  '0%,100%': { transform: 'rotate(0)' },
                  '25%': { transform: 'rotate(-20deg)' },
                  '75%': { transform: 'rotate(20deg)' },
                }
              }} />
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#FF6B00', lineHeight: 1 }}>
                  PLING! 🔔
                </Typography>
                <Typography variant="body2" sx={{ color: '#ccc', mt: 0.3 }}>
                  {message}
                </Typography>
                {(leadTitle || companyName) && (
                  <Typography variant="caption" sx={{ color: '#aaa', mt: 0.5, display: 'block' }}>
                    {[leadTitle, companyName].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      </Slide>
    );
  }

  // KLING-KLANG – center overlay with confetti
  return (
    <Fade in={visible}>
      <Box sx={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: 'rgba(0,0,0,0.6)',
        cursor: 'pointer',
        backdropFilter: 'blur(4px)',
      }}
        onClick={() => { setVisible(false); setTimeout(onClose, 300); }}>

        {/* Confetti */}
        <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          {particles.map(p => (
            <Particle key={p.id} color={p.color} delay={p.delay} left={p.left} size={p.size} />
          ))}
        </Box>

        <Box sx={{
          textAlign: 'center', p: 5, borderRadius: 4,
          bgcolor: 'white', maxWidth: 480, width: '90%',
          animation: 'popIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          '@keyframes popIn': {
            '0%': { transform: 'scale(0.5)', opacity: 0 },
            '100%': { transform: 'scale(1)', opacity: 1 },
          },
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Colored top bar */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6,
            background: 'linear-gradient(90deg, #FF6B00, #FFD700, #4CAF50, #2196F3, #9C27B0)' }} />

          <Box sx={{ fontSize: '4rem', lineHeight: 1.1, mb: 1 }}>🏆</Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1.5 }}>
            <EmojiEventsIcon sx={{
              fontSize: 32, color: '#FFD700',
              animation: 'bounce 0.5s ease infinite alternate',
              '@keyframes bounce': {
                '0%': { transform: 'translateY(0)' },
                '100%': { transform: 'translateY(-8px)' },
              }
            }} />
            <CelebrationIcon sx={{
              fontSize: 32, color: '#FF6B00',
              animation: 'bounce 0.5s ease 0.15s infinite alternate',
              '@keyframes bounce': {
                '0%': { transform: 'translateY(0)' },
                '100%': { transform: 'translateY(-8px)' },
              }
            }} />
            <EmojiEventsIcon sx={{
              fontSize: 32, color: '#FFD700',
              animation: 'bounce 0.5s ease 0.3s infinite alternate',
              '@keyframes bounce': {
                '0%': { transform: 'translateY(0)' },
                '100%': { transform: 'translateY(-8px)' },
              }
            }} />
          </Box>

          <Typography variant="h3" fontWeight="900"
            sx={{ color: '#1a1a1a', letterSpacing: -1, mb: 0.5 }}>
            KLING-KLANG!
          </Typography>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#FF6B00', mb: 2 }}>
            {message}
          </Typography>

          {leadTitle && (
            <Typography variant="h6" sx={{ color: '#1a1a1a', mb: 0.5 }}>
              {leadTitle}
            </Typography>
          )}
          {companyName && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {companyName}
            </Typography>
          )}
          {value && (
            <Box sx={{
              display: 'inline-block', px: 3, py: 1, borderRadius: 10,
              bgcolor: '#E8F5E9', mt: 1,
            }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#2E7D32' }}>
                {Number(value).toLocaleString('sv-SE')} kr
              </Typography>
            </Box>
          )}

          <Typography variant="caption" color="text.disabled"
            sx={{ display: 'block', mt: 3 }}>
            Klicka för att stänga
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
}
